
import { useParams, useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DetailSection } from "@/components/freelancers/DetailSection";
import { UserPlus, ArrowLeft, Pencil, GripVertical, Trash2, Building2, Briefcase, DollarSign, MonitorPlay, Calendar, MapPin, Clock, FileText, ListChecks, ExternalLink, Mail, Eye, EyeOff, Zap, Columns, Bot } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { Vaga } from "@/types/vaga";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect, useMemo } from "react";
import { VagaForm } from "@/components/vagas/VagaForm";
import { VagaFreelancers2Tab } from "@/components/vagas/kanban/VagaFreelancers2Tab";
import { VagaObservacoes } from "@/components/vagas/VagaObservacoes";
import { VagaDetailsSkeleton } from "@/components/vagas/VagaDetailsSkeleton";
import { RecommendationSystem } from "@/components/recommendations/RecommendationSystem";
import { useKanbanApi } from "@/utils/kanban-api";
import { formatDateBR } from "@/utils/dateUtils";
import { VagaAssistantPanel } from "@/components/vaga-assistant/VagaAssistantPanel";
import { toUserError } from "@/lib/api-error";

// Função para formatar URLs
const formatUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

// Função para gerar iniciais do nome (máximo 2 letras)
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Função para formatar verba range
const formatVerbaRange = (verbaMinima?: number | null, verbaMaxima?: number | null, verbaLegacy?: string | null): string => {
  if (verbaMinima && verbaMaxima) {
    const min = (verbaMinima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const max = (verbaMaxima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    return `${min} - ${max}`;
  }
  if (verbaMinima) {
    return `A partir de ${(verbaMinima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
  }
  if (verbaMaxima) {
    return `Até ${(verbaMaxima / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
  }
  return verbaLegacy || "";
};

// Função para calcular duração no status
const getStatusDuration = (lastStatusChanged: string | null | undefined): string => {
  if (!lastStatusChanged) return "";
  
  const now = new Date();
  const statusChangeDate = new Date(lastStatusChanged);
  const diffMs = now.getTime() - statusChangeDate.getTime();
  
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (months > 0) return `${months} mes${months === 1 ? '' : 'es'}`;
  if (weeks > 0) return `${weeks} semana${weeks === 1 ? '' : 's'}`;
  if (days > 0) return `${days} dia${days === 1 ? '' : 's'}`;
  if (hours > 0) return `${hours} hora${hours === 1 ? '' : 's'}`;
  return `${minutes}min`;
};

// Função para obter cor do badge de duração
const getStatusBadgeColor = (lastStatusChanged: string | null | undefined): string => {
  if (!lastStatusChanged) return "bg-blue-50 text-blue-700 border-blue-200";
  
  const now = new Date();
  const statusChangeDate = new Date(lastStatusChanged);
  const diffMs = now.getTime() - statusChangeDate.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (months >= 1) return "bg-red-50 text-red-700 border-red-200";
  if (weeks >= 1) return "bg-orange-50 text-orange-700 border-orange-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
};

const ABAS_DA_VAGA = ["dados", "recomendacoes", "kanban", "freelancers", "observacoes"];

// Links antigos usavam "freelancers2"; aba desconhecida deixava a tela em branco.
function abaDaVagaValida(aba: string | null): string {
  if (aba === "freelancers2") return "freelancers";
  return aba && ABAS_DA_VAGA.includes(aba) ? aba : "dados";
}

export default function VagaDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingVaga, setEditingVaga] = useState<Partial<Vaga>>({});

  const [mainTabValue, setMainTabValue] = useState(() => abaDaVagaValida(searchParams.get("tab")));
  const [selectedEtapaIdFromUrl, setSelectedEtapaIdFromUrl] = useState<string | null>(searchParams.get("etapaId"));


  // Assistente IA
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);



  // Limpar parâmetros da URL após carregar a página com a tab/etapa corretas
  useEffect(() => {
    if (searchParams.has("tab") || searchParams.has("etapaId")) {
      const timer = setTimeout(() => {
        // Limpar os parâmetros da URL mantendo apenas o histórico limpo
        const newParams = new URLSearchParams();
        setSearchParams(newParams, { replace: true });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  const { data: vaga, isLoading, isError, refetch } = useQuery({
    queryKey: ["vaga", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vagas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Vaga;
    },
  });

  // Buscar métricas do kanban para o badge
  const kanbanApi = useKanbanApi();
  const { data: kanbanMetricas } = useQuery({
    queryKey: ["kanban-metricas", id],
    queryFn: () => kanbanApi.getMetricas(id || ""),
    enabled: !!id,
  });

  // Dados da agência da vaga (nome) — via query cacheada em vez de useEffect
  // imperativo. Depende do agencia_id que vem da vaga, mas com cache e sem
  // efeito colateral manual.
  const { data: agenciaData } = useQuery({
    queryKey: ["agencia", vaga?.agencia_id],
    enabled: !!vaga?.agencia_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .eq("id", vaga!.agencia_id!)
        .single();
      if (error) throw error;
      return data as { id: string; nome: string };
    },
  });

  const updateVagaMutation = useMutation({
    mutationFn: async (updatedVaga: Partial<Vaga>) => {
      console.log("Atualizando vaga:", { 
        id, 
        dados: updatedVaga 
      });
      
      // area_id lives in vaga_areas, not in the vagas table
      const { area_id: _areaId, ...dataToUpdate } = updatedVaga;
      
      // Remove properties that should not be included in the update
      delete dataToUpdate.created_at;
      delete dataToUpdate.updated_at;
      
      if (dataToUpdate.id && dataToUpdate.id !== id) {
        delete dataToUpdate.id;
      }
      
      // Start a transaction to update both the vaga and the vaga_areas
      const { error } = await supabase
        .from("vagas")
        .update(dataToUpdate)
        .eq("id", id);

      if (error) {
        console.error("Erro detalhado na atualização:", error);
        throw error;
      }
      
      return dataToUpdate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vaga", id] });
      queryClient.invalidateQueries({ queryKey: ["freelancers-selecionados", id] });
      setIsEditModalOpen(false);
      toast({
        title: "Sucesso",
        description: "Vaga atualizada com sucesso!",
      });
    },
    onError: (error) => {
      console.error("Erro ao atualizar vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a vaga. " + toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVaga) return;
    updateVagaMutation.mutate(editingVaga);
  };

  if (isLoading) {
    return <VagaDetailsSkeleton />;
  }

  if (isError || !vaga) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/vagas")} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
        {isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <EmptyState
            icon={Briefcase}
            title="Vaga não encontrada"
            description="Ela pode ter sido removida ou o link está incorreto."
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            className="gap-2"
            onClick={() => navigate("/vagas")}
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              setEditingVaga(vaga || {});
              setIsEditModalOpen(true);
            }}
          >
            <Pencil className="h-4 w-4" />
            Editar Vaga
          </Button>
        </div>

        {/* Header da Vaga */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-6">
              {/* Informações principais (esquerda) */}
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Building2 className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">
                    {vaga?.agencia || agenciaData?.nome || "Agência não informada"}
                  </h1>
                  {vaga?.cliente && (
                    <p className="mt-1 text-muted-foreground">{vaga.cliente}</p>
                  )}
                  {(vaga?.areas || vaga?.descricao_curta) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      {vaga?.areas && <Badge variant="secondary">{vaga.areas}</Badge>}
                      {vaga?.descricao_curta && (
                        <span className="text-xs text-muted-foreground">
                          {vaga.descricao_curta}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Informações secundárias (direita) */}
              <div className="flex flex-wrap gap-3 md:max-w-md">
                {formatVerbaRange(vaga?.verba_minima, vaga?.verba_maxima, vaga?.verba) && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-green-800 font-medium text-sm">
                      {formatVerbaRange(vaga?.verba_minima, vaga?.verba_maxima, vaga?.verba)}
                    </span>
                  </div>
                )}
                
                {vaga?.formato && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-200">
                    <MonitorPlay className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-800 font-medium text-sm">{vaga.formato}</span>
                  </div>
                )}

                {vaga?.tipo_contrato && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">{vaga.tipo_contrato}</span>
                  </div>
                )}

                {vaga?.modalidade && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">
                      {vaga.modalidade === 'frila' ? 'Frila' : 'Fixa'}
                    </span>
                  </div>
                )}

                {vaga?.data_inicio && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">
                      {formatDateBR(vaga.data_inicio)}
                    </span>
                  </div>
                )}

                {(vaga?.duracao_dias || vaga?.duracao) && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">
                      Duração: {vaga.duracao_dias 
                        ? `${vaga.duracao_dias} ${vaga.duracao_dias === 1 ? 'dia' : 'dias'}`
                        : vaga.duracao
                      }
                    </span>
                  </div>
                )}

                {vaga?.localizacao_cidade && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">
                      {[
                        vaga.localizacao_cidade,
                        vaga.localizacao_bairro,
                        vaga.localizacao_estado
                      ].filter(Boolean).join(' - ')}
                    </span>
                  </div>
                )}

                {vaga?.regime_contratacao && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg border border-border">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-foreground font-medium text-sm">{vaga.regime_contratacao}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Abas de Conteúdo */}
        <Card>
          <Tabs value={mainTabValue} onValueChange={setMainTabValue} className="w-full">
            <TabsList className="grid w-[calc(100%-3rem)] grid-cols-3 bg-muted p-1 rounded-lg mx-6 mt-6">
              <TabsTrigger 
                value="dados" 
                className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-xs"
              >
                <Building2 className="h-4 w-4" />
                Descrição da Vaga
              </TabsTrigger>
              {/*<TabsTrigger 
                value="recomendacoes" 
                className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-xs"
              >
                <Zap className="h-4 w-4" />
                Recomendações IA
              </TabsTrigger> */}

              {/* <TabsTrigger 
                value="kanban" 
                className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-xs"
              >
                <Columns className="h-4 w-4" />
                Kanban
              </TabsTrigger> */}
              <TabsTrigger 
                value="freelancers" 
                className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-xs"
              >
                <UserPlus className="h-4 w-4" />
                Freelancers
                {kanbanMetricas && kanbanMetricas.total_ativos > 0 && (
                  <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    {kanbanMetricas.total_ativos}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="observacoes" 
                className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-xs"
              >
                <FileText className="h-4 w-4" />
                Observações
              </TabsTrigger>
            </TabsList>

            {/* Aba: Dados da Vaga */}
            <TabsContent value="dados" className="p-6">
              {(vaga?.descricao?.trim() || vaga?.requisitos?.trim()) ? (
                <div className="space-y-6">
                  {vaga?.descricao?.trim() && (
                    <DetailSection
                      title="Descrição"
                      icon={FileText}
                      className="border-t-0 pt-0"
                    >
                      <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                        {vaga.descricao.trim()}
                      </p>
                    </DetailSection>
                  )}

                  {vaga?.requisitos?.trim() && (
                    <DetailSection
                      title="Requisitos"
                      icon={ListChecks}
                      className={vaga?.descricao?.trim() ? undefined : "border-t-0 pt-0"}
                    >
                      <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                        {vaga.requisitos.trim()}
                      </p>
                    </DetailSection>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={FileText}
                  title="Nenhuma descrição ou requisito cadastrado"
                  description={'Use o botão "Editar Vaga" para adicionar informações.'}
                />
              )}
            </TabsContent>

            {/* Aba: Recomendações IA */}
            <TabsContent value="recomendacoes" className="p-6">
              <RecommendationSystem vagaId={id || ""} />
            </TabsContent>

            {/* Aba: Freelancers */}
            <TabsContent value="freelancers" className="p-6">
              <VagaFreelancers2Tab vagaId={id || ""} initialEtapaId={selectedEtapaIdFromUrl || undefined} />
            </TabsContent>

            {/* Aba: Observações */}
            <TabsContent value="observacoes" className="p-6">
              <VagaObservacoes vagaId={id || ""} />
            </TabsContent>
          </Tabs>
        </Card>

      {/* Botão flutuante do Assistente IA */}
      <Button
        onClick={() => setIsAssistantOpen(true)}
        className="fixed bottom-6 right-6 h-12 w-12 rounded-full shadow-lg bg-purple-600 hover:bg-purple-700 z-40"
        size="icon"
        title="Assistente IA de Seleção"
      >
        <Bot className="h-5 w-5 text-white" />
      </Button>

      {/* Painel do Assistente IA */}
      <VagaAssistantPanel
        vagaId={id || ""}
        open={isAssistantOpen}
        onOpenChange={setIsAssistantOpen}
      />

      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Editar Vaga</DialogTitle>
            <DialogDescription>
              Atualize as informações da vaga. Todos os campos são opcionais.
            </DialogDescription>
          </DialogHeader>
          <VagaForm
            novaVaga={editingVaga}
            setNovaVaga={setEditingVaga}
            onSubmit={handleSubmit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
